import { useMutation } from '@apollo/client/react';
import { useAuth } from 'react-oidc-context';
import { SEND_LOCALLY_GENERATED_REQUEST_MUTATION } from './useLocalExport.gql';
import { useSendRequests } from './useSendRequests';

export const useLocalExport = () => {
  const auth = useAuth();
  const sendRequests = useSendRequests();
  const [sendLocallyGeneratedMutation] = useMutation(SEND_LOCALLY_GENERATED_REQUEST_MUTATION);

  const generateLocally = async (requestIds: string[]) => {
    const response = await sendRequests(requestIds, true);
    return response.data?.sendRequests;
  };

  const exportOrderZip = async (orderId: string) => {
    if (!window.nativeApi) {
      throw new Error('Native API not available - only works in desktop client');
    }

    const response = await fetch(`/api/rest/order-export/download-local/${orderId}`, {
      headers: { authorization: `Bearer ${auth.user?.access_token}` },
    });

    if (!response.ok) {
      throw new Error(`Failed to download ZIP: ${response.statusText}`);
    }

    const zipBlob = await response.blob();
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(zipBlob);

    for (const [filename, file] of Object.entries(zip.files)) {
      if (!file.dir) {
        const buffer = await file.async('arraybuffer');
        await window.nativeApi.aisExport(filename, new Uint8Array(buffer));
      }
    }
  };

  const generateAndExport = async (requestIds: string[]) => {
    const requests = await generateLocally(requestIds);

    if (!requests) {
      throw new Error('Failed to generate requests locally');
    }

    for (const request of requests) {
      for (const order of request.orders) {
        await exportOrderZip(order.id);
      }
    }

    return requests;
  };

  const sendLocallyGenerated = async (requestId: string) => {
    const response = await sendLocallyGeneratedMutation({
      variables: { requestId },
    });
    return response.data?.sendLocallyGeneratedRequest;
  };

  return {
    exportOrderZip,
    generateAndExport,
    sendLocallyGenerated,
  };
};
